import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineCommentExclamationIcon from 'LuxKit/Icons/line/LineCommentExclamationIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ContactContext from 'contexts/contactContext'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import { rem } from 'polished'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalContext from 'contexts/ModalContext'
import type { ChatHistoryModalResult } from '../History/ChatsHistoryModal'
import ChatsHistoryModal from '../History/ChatsHistoryModal'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { NewChatModalResult } from './NewChatModal'
import NewChatModal from './NewChatModal'
import { createNewChat, postMessageFeedback, setCurrentChatId } from 'actions/supportAssistant/supportAssistantActions'
import FeedbackModal from './FeedbackModal'
import { SocketContext } from 'supportAssistant/contexts/SocketContext'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'

const HeaderGroup = styled(Group)`
  height: ${rem(60)};
`

function ChatHeader() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { onLogin } = useContext(SocketContext)

  const customerId = useAppSelector(state => state.auth.account.memberId)
  const currentChatId = useAppSelector(state => state.supportAssistant.currentChatId) ?? 'default'

  const showModal = useContext(ModalContext)

  const { value: open, toggle, off } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local

  // Infinite members call their dedicated concierge from the header rather than the regional support line.
  const isInfiniteMember = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const concierge = useInfiniteConciergeDetails()
  const callSupportHref = isInfiniteMember && concierge ? concierge.phoneHref : `tel:${localContact.number}`

  const handleNewChatClick = useCallback(async() => {
    if (!customerId && currentChatId) {
      const result = await showModal<NewChatModalResult>(<NewChatModal />)
      if (result?.triggerLogin) {
        onLogin()
      }
    } else {
      dispatch(createNewChat())
    }
  }, [currentChatId, customerId, dispatch, onLogin, showModal])

  const showChatHistoryModal = useCallback(async() => {
    const result = await showModal<ChatHistoryModalResult>(<ChatsHistoryModal />)
    if (result?.chatId) {
      dispatch(setCurrentChatId(result.chatId))
    }
  }, [dispatch, showModal])

  const onFeedbackClick = useCallback(async() => {
    const userFeedback = await showModal<undefined | any>(<FeedbackModal />)

    if (userFeedback?.issues?.length) {
      dispatch(postMessageFeedback({
        chatId: currentChatId,
        customerId,
        ...userFeedback,
      }))
    }
  }, [currentChatId, customerId, dispatch, showModal])

  return (
    <HeaderGroup direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
      <Group direction="horizontal">
        <CSSBreakpoint min="tablet">
          <TextButton
            kind="tertiary"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'rHuv9WXssnemPOC8QP6n',
              defaultMessage: 'show history',
              description: 'Aria label for button to show chat history on tablet',
            })}
            onClick={showChatHistoryModal}
            startIcon={<LineBarsIcon />}
          >
            {intl.formatMessage({
              id: '9L1r4v3eWpevhRKoYb15',
              defaultMessage: 'Chat History',
              description: 'Action button label to open chat history panel in the customer support assistant',
            })}
          </TextButton>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <IconButton
            kind="tertiary"
            variant="default"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'q9RaT98SUXCK_79jHG0v',
              defaultMessage: 'show history',
              description: 'Aria label for button to show chat history on mobile',
            })}
            onClick={showChatHistoryModal}
          >
            <LineBarsIcon />
          </IconButton>
          <IconButton
            kind="tertiary"
            variant="default"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: 'BO3pPvvZrFy1P99fgtfI',
              defaultMessage: 'call support',
              description: 'Accessibility aria-label for button to call support on mobile',
            })}
            href={callSupportHref}
            rel="nofollow"
          >
            <LinePhoneIcon />
          </IconButton>
        </CSSBreakpoint>
      </Group>

      <CSSBreakpoint min="tablet">
        <HeadingTextBlock variant="heading6">
          {intl.formatMessage({
            id: 'p4xBccjc3_8fHExaeHGk',
            defaultMessage: 'Luxury Escapes Support',
            description: 'Heading text for the support chat header in the support area',
          })}
        </HeadingTextBlock>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <ClickableLink to="/">
          <LuxuryEscapesSmallLogo height={32} width={32} />
        </ClickableLink>
      </CSSBreakpoint>

      <Group direction="horizontal" horizontalAlign="end">
        <CSSBreakpoint min="tablet">
          <TextButton
            kind="tertiary"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: '1YyOysafw9tVp9G37YEi',
              defaultMessage: 'open new chat',
              description: 'Aria label for button to open a new chat on tablet',
            })}
            onClick={handleNewChatClick}
            startIcon={<LinePlusIcon />}
          >
            {intl.formatMessage({
              id: '_5F40BB1Iz136WCZSmc7',
              defaultMessage: 'New Chat',
              description: 'Action button label to start a new chat in the customer support assistant',
            })}
          </TextButton>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <IconButton
            kind="tertiary"
            variant="default"
            data-testid="action-menu-button"
            aria-label={intl.formatMessage({
              id: '-CRx7lOv21Nzp6Rmt67b',
              defaultMessage: 'open new chat',
              description: 'Aria label for button to open a new chat on mobile',
            })}
            onClick={handleNewChatClick}
          >
            <LinePlusIcon />
          </IconButton>
        </CSSBreakpoint>

        <IconButton
          kind="tertiary"
          variant="default"
          data-testid="action-menu-button"
          aria-label={intl.formatMessage({
            id: 'J_rNR1LSlg54IzrZPfMO',
            defaultMessage: 'chat action menu',
            description: 'Accessibility aria-label for chat action overflow menu button',
          })}
          onClick={toggle}
          ref={triggerRef}
        >
          <LineEllipsisVIcon />
        </IconButton>
        <DropdownList
          size="S"
          open={open}
          triggerRef={triggerRef}
          anchorRef={triggerRef}
          onClose={off}
        >
          <ClickableListItem
            disabled={currentChatId === 'default'}
            size="medium"
            startIcon={<LineCommentExclamationIcon />}
            title={intl.formatMessage({
              id: '0AUe5kGR1Owj6EhzX4bS',
              defaultMessage: 'Report a problem',
              description: 'Menu item to report a problem with support chat in the customer support assistant',
            })}
            onClick={onFeedbackClick}
          />
        </DropdownList>
      </Group>
    </HeaderGroup>
  )
}

export default ChatHeader
